import useApi from "@/hooks/useApi";
import React, { useMemo, useState } from "react";
import useTeams from "@/hooks/useTeams";
import {
  Column,
  TableBase,
  Team
} from "@velaro/velaro-component-lib";
import ActionBar from "./ActionBar";
import SearchBar from "@/components/SearchBar";
import EditTeamPanel from "./EditTeamPanel";
import CreateTeamPanel from "./CreateTeamPanel";

const columns: Column[] = [
  { label: "Name", accessor: "name" },
  { label: "Id", accessor: "id" },
  { label: "Routing Action", accessor: "routingAction" },
  { label: "Auto Routing Type", accessor: "autoRoutingType" },
  { label: "Unassignment Action", accessor: "unassignmentAction" },
  { label: "Auto Unassignment Warning", accessor: "autoUnassignmentWarning" },
  { label: "Actions", accessor: "actions", disableSort: true }
];

export default function Teams() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTeamId, setEditTeamId] = useState<number>();
  const [searchString, setSearchString] = useState("");
  const [selectedIndicies, setSelectedIndicies] = useState<number[]>([]);
  const api = useApi();

  const { teams, setTeams } = useTeams();

  async function deleteTeam(id: number) {
    if (teams.length === 1) {
      alert("Unable to delete team. You must always have at least one team.");
      return;
    }
    if (
      confirm(
        "Are you sure you want to delete this team? Deleting this team could cause users to no longer be apart of a team."
      )
    ) {
      await api.messaging.delete(`Teams/${id}`);
      setTeams(teams.filter((x) => x.id !== id));
    }
  }

  const filteredTeams = useMemo(
    () =>
      teams.filter((val) => {
        if (searchString === "") {
          return val;
        } else if (
          val.name.toLowerCase().includes(searchString.toLowerCase()) ||
          val.id
            .toString()
            .toLowerCase()
            .includes(searchString.toLowerCase()) ||
          val.routingAction
            .toLowerCase()
            .includes(searchString.toLowerCase()) ||
          val.routingType.toLowerCase().includes(searchString.toLowerCase()) ||
          val.unassignAction
            .toLowerCase()
            .includes(searchString.toLowerCase()) ||
          val.unassignWarningTime
            .toString()
            .toLowerCase()
            .includes(searchString.toLowerCase())
        ) {
          return val;
        }
      }),
    [teams, searchString]
  );

  const tableData = useMemo(() => {
    return filteredTeams.map((team) => ({
      ...team,
      name: team.name,
      id: team.id,
      routingAction: team.routingAction,
      autoRoutingType: team.routingType,
      unassignmentAction: team.unassignAction,
      autoUnassignmentWarning: team.unassignWarningTime,
      actions: (
        <>
          <ActionBar
            onEdit={() => setEditTeamId(team.id)}
            onCopy={() => {}}
            onDelete={() => deleteTeam(team.id!)}
          />
        </>
      )
    }));
  }, [filteredTeams, editTeamId, deleteTeam]);
  return (
    <>
      {editTeamId === undefined && showCreateModal === false && (
        <>
          <div className="px-6 pt-5 pb-4 flex items-center gap-3 border-b border-slate-200 bg-white">
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-slate-900 tracking-tight">Teams</h1>
              <p className="text-xs text-slate-400 mt-0.5">Manage agent teams and their routing assignments</p>
            </div>
          </div>

          <div className="px-6 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3 flex-wrap">
            <SearchBar
              onSearch={setSearchString}
              placeholderText="Search"
            />
            <div className="ml-auto">
              <button
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity shadow-sm"
                onClick={() => setShowCreateModal(true)}
              >
                + Add Team
              </button>
            </div>
          </div>

          <div className="overflow-auto h-full">
            <TableBase
              columns={columns}
              data={tableData}
              onChangeSelection={setSelectedIndicies}
            />
          </div>
        </>
      )}

      {showCreateModal && (
        <CreateTeamPanel
          show={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onTeamCreated={(team: Team) => {
            setShowCreateModal(false);
            setTeams([...teams, team]);
          }}
        />
      )}
      {editTeamId && (
        <EditTeamPanel
          teamId={editTeamId}
          onClose={() => setEditTeamId(undefined)}
          onTeamUpdated={(team: Team) => {
            setEditTeamId(undefined);
            setTeams(teams.map((x) => (x.id === team.id ? team : x)));
          }}
        />
      )}
    </>
  );
}

const thClassName = "border p-2 text-left";
const tdClassName = "border p-2";
